import { useState, useImperativeHandle, forwardRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Target, Plus, CheckCircle2, Calendar, TrendingUp } from "lucide-react";

const HealthGoalsTracker = forwardRef(function HealthGoalsTracker(props, ref) {
  const [goals, setGoals] = useState([
    {
      id: 1,
      title: "Lower Cholesterol",
      target: 180,
      current: 220,
      unit: "mg/dL",
      deadline: "2024-06-01",
      category: "cardiovascular",
      progress: 65,
    },
    {
      id: 2,
      title: "Increase Vitamin D",
      target: 40,
      current: 25,
      unit: "ng/mL",
      deadline: "2024-04-15",
      category: "nutritional",
      progress: 45,
    },
    {
      id: 3,
      title: "Optimize Sleep",
      target: 8,
      current: 6.5,
      unit: "hours",
      deadline: "2024-03-30",
      category: "lifestyle",
      progress: 80,
    },
  ]);

  const [showAddGoal, setShowAddGoal] = useState(false);
  const [newGoal, setNewGoal] = useState({
    title: "",
    target: "",
    current: "",
    unit: "",
    deadline: "",
    category: "health",
  });

  useImperativeHandle(ref, () => ({
    toggleAddGoalForm: () => {
      setShowAddGoal((prev) => !prev);
    },
  }));

  const categoryColors = {
    cardiovascular: "bg-red-100 text-red-700",
    nutritional: "bg-green-100 text-green-700",
    lifestyle: "bg-blue-100 text-blue-700",
    hormonal: "bg-purple-100 text-purple-700",
    metabolic: "bg-orange-100 text-orange-700",
    health: "bg-gray-100 text-gray-700",
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return "text-green-600";
    if (progress >= 60) return "text-blue-600";
    if (progress >= 40) return "text-orange-600";
    return "text-red-600";
  };

  const handleAddGoal = () => {
    if (newGoal.title && newGoal.target && newGoal.current) {
      const goal = {
        id: goals.length + 1,
        ...newGoal,
        target: parseFloat(newGoal.target),
        current: parseFloat(newGoal.current),
        progress: Math.min(
          100,
          (parseFloat(newGoal.current) / parseFloat(newGoal.target)) * 100
        ),
      };
      setGoals([...goals, goal]);
      setNewGoal({
        title: "",
        target: "",
        current: "",
        unit: "",
        deadline: "",
        category: "health",
      });
      setShowAddGoal(false);
    }
  };

  const updateGoalProgress = (goalId, newCurrent) => {
    setGoals(
      goals.map((goal) => {
        if (goal.id === goalId) {
          const progress = Math.min(100, (newCurrent / goal.target) * 100);
          return { ...goal, current: newCurrent, progress };
        }
        return goal;
      })
    );
  };

  return (
    <Card
      className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl"
      ref={ref}
    >
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-green-600" />
            Health Goals
          </CardTitle>
          <Button
            size="sm"
            onClick={() => setShowAddGoal(!showAddGoal)}
            className="bg-green-600 hover:bg-green-700"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Goal
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {showAddGoal && (
          <div className="mb-6 p-4 bg-slate-50 rounded-lg space-y-3">
            <Input
              placeholder="Goal title (e.g., Lower Blood Pressure)"
              value={newGoal.title}
              onChange={(e) =>
                setNewGoal({ ...newGoal, title: e.target.value })
              }
            />
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Target value"
                type="number"
                value={newGoal.target}
                onChange={(e) =>
                  setNewGoal({ ...newGoal, target: e.target.value })
                }
              />
              <Input
                placeholder="Current value"
                type="number"
                value={newGoal.current}
                onChange={(e) =>
                  setNewGoal({ ...newGoal, current: e.target.value })
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Unit (mg/dL, hours, etc.)"
                value={newGoal.unit}
                onChange={(e) =>
                  setNewGoal({ ...newGoal, unit: e.target.value })
                }
              />
              <Input
                type="date"
                value={newGoal.deadline}
                onChange={(e) =>
                  setNewGoal({ ...newGoal, deadline: e.target.value })
                }
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAddGoal}>
                Save Goal
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowAddGoal(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {goals.map((goal) => (
            <div
              key={goal.id}
              className="p-4 bg-slate-50/60 rounded-lg space-y-3"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-semibold text-slate-900">{goal.title}</h4>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Calendar className="w-3 h-3" />
                    Target: {new Date(goal.deadline).toLocaleDateString()}
                  </div>
                </div>
                <Badge
                  className={`${categoryColors[goal.category] || categoryColors.health} text-xs`}
                >
                  {goal.category}
                </Badge>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>
                    Current: {goal.current} {goal.unit}
                  </span>
                  <span>
                    Target: {goal.target} {goal.unit}
                  </span>
                </div>
                <Progress value={goal.progress} className="h-2" />
                <div className="flex justify-between items-center">
                  <span
                    className={`text-sm font-medium ${getProgressColor(goal.progress)}`}
                  >
                    {goal.progress.toFixed(0)}% Complete
                  </span>
                  {goal.progress >= 100 && (
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                <Input
                  placeholder="Update current value"
                  type="number"
                  className="text-xs"
                  onBlur={(e) => {
                    if (e.target.value) {
                      updateGoalProgress(goal.id, parseFloat(e.target.value));
                      e.target.value = "";
                    }
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-green-600" />
            <span className="font-medium text-green-900">Progress Summary</span>
          </div>
          <p className="text-sm text-green-800">
            {goals.filter((g) => g.progress >= 100).length} of {goals.length}{" "}
            goals completed. Keep up the great work!
          </p>
        </div>
      </CardContent>
    </Card>
  );
});

export default HealthGoalsTracker;
